/* ==========================================================================
   Admin Provider Details Page (PORTAL-74)
   Everything the pharmacy has on file for one provider — contact details,
   credentials, practice addresses, catalog access, setting overrides and
   account state — alongside the same actions the Providers list offers.
   Read-only: each section links to the existing tool that edits it.
   ========================================================================== */

import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Settings,
  Package,
  MapPin,
  Mail,
  UserX,
  UserCheck,
  Trash2,
  ShieldOff,
} from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { CatalogAssignmentModal } from '@/components/admin/CatalogAssignmentModal'
import { ProviderAddressesModal } from '@/components/admin/ProviderAddressesModal'
import { ProviderStatusBadge } from '@/components/admin/ProviderStatusBadge'
import {
  DeleteProviderModal,
  DisableProviderModal,
  ResetProviderTwoFactorModal,
} from '@/components/admin/ProviderConfirmModals'
import {
  PracticeAddressManager,
  type PracticeAddressManagerHandlers,
} from '@/components/forms/PracticeAddressManager'
import { useAdminAuth, useAdminPermissions } from '@/hooks/useAdminAuth'
import { useToast } from '@/context/ToastContext'
import {
  getProvider,
  getProviderSettings,
  getProviderCatalogAssignments,
  updateProviderCatalogAssignments,
  updateProvider,
  deleteProvider,
  resendProviderInvite,
  resetProviderTwoFactor,
  listCatalog,
  adminListProviderAddresses,
  adminCreateProviderAddress,
  adminUpdateProviderAddress,
  adminDeleteProviderAddress,
} from '@/api/admin'
import { ApiClientError } from '@/api/client'
import { ProviderStatus } from '@/api/types'
import type {
  CatalogProductResponse,
  ProviderDetailResponse,
  ProviderResponse,
  ProviderSettingsResponse,
} from '@/api/types'
import { DEFAULT_COUNTRY, isCanada, regionLabel } from '@/utils/countries'
import { credentialTypeLabel, requiresSupervisingPhysician } from '@/utils/credentials'
import { formatPhoneForDisplay } from '@/utils/phone'
import {
  PAYMENT_DELIVERY_REQUIRED_SETTINGS,
  PAYMENT_DELIVERY_SETTINGS,
  generalSettingsFor,
  overridesFromResponse,
} from '@/utils/providerSettings'

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

function formatDateTime(dateString: string | null): string {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiClientError ? err.getUserMessage() : fallback
}

/* --------------------------------------------------------------------------
   Layout pieces
   -------------------------------------------------------------------------- */

/** One label/value row; an empty value renders as a muted dash. */
function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  const empty = value === null || value === undefined || value === ''
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="text-charcoal/60 shrink-0">{label}</dt>
      <dd className="text-charcoal break-words sm:text-right">
        {empty ? <span className="text-gray-400">—</span> : value}
      </dd>
    </div>
  )
}

/** Card header with the section title on the left and an action on the right. */
function SectionHeader({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span>{title}</span>
      {action}
    </div>
  )
}

/* --------------------------------------------------------------------------
   Page
   -------------------------------------------------------------------------- */

export function ProviderDetail() {
  const { pharmacy } = useAdminAuth()
  const { providerId } = useParams<{ providerId: string }>()
  const navigate = useNavigate()
  const { success: showSuccess, error: showError } = useToast()

  const slug = pharmacy?.portal_slug
  const country = pharmacy?.country
  // Canadian pharmacies have no NPI / DEA (PORTAL-161)
  const usCredentials = !isCanada(country)
  const providersPath = slug ? `/rx/${slug}/admin/providers` : ''

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [provider, setProvider] = useState<ProviderDetailResponse | null>(null)
  const [settings, setSettings] = useState<ProviderSettingsResponse | null>(null)
  const [assignedProductIds, setAssignedProductIds] = useState<string[]>([])

  // Practice addresses: the read-only list only loads on mount, so it's
  // remounted (key bump) after the manage modal closes.
  const [addressesKey, setAddressesKey] = useState(0)
  const [addressesModalOpen, setAddressesModalOpen] = useState(false)

  // Catalog assignment modal
  const [catalogModalOpen, setCatalogModalOpen] = useState(false)
  const [catalogProducts, setCatalogProducts] = useState<CatalogProductResponse[]>([])
  const [isSavingCatalog, setIsSavingCatalog] = useState(false)

  // Account actions
  const [isResending, setIsResending] = useState(false)
  const [isEnabling, setIsEnabling] = useState(false)
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false)
  const [isDisabling, setIsDisabling] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  // Reset 2FA (PORTAL-187) — Admins and Managers only, mirroring the backend.
  const { canManage } = useAdminPermissions()
  const [resetTwoFactorOpen, setResetTwoFactorOpen] = useState(false)
  const [isResettingTwoFactor, setIsResettingTwoFactor] = useState(false)

  const load = useCallback(async () => {
    if (!slug || !providerId) {
      // Nothing to load yet (admin pharmacy/route not resolved) — clear the
      // initial spinner so the page doesn't hang on "Loading…".
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [providerRes, settingsRes, catalogRes] = await Promise.all([
        getProvider(slug, providerId),
        getProviderSettings(slug, providerId),
        getProviderCatalogAssignments(slug, providerId),
      ])
      setProvider(providerRes)
      setSettings(settingsRes)
      setAssignedProductIds(catalogRes.product_ids)
    } catch (err) {
      setError(errorMessage(err, 'Failed to load provider details.'))
    } finally {
      setLoading(false)
    }
  }, [slug, providerId])

  useEffect(() => {
    load()
  }, [load])

  // Re-read just the provider (no page spinner) after a change elsewhere —
  // e.g. an address edit can change the clinic summary and profile completeness.
  async function refreshProvider() {
    if (!slug || !providerId) return
    try {
      setProvider(await getProvider(slug, providerId))
    } catch (err) {
      showError(errorMessage(err, 'Failed to refresh provider details.'))
    }
  }

  // The list-style endpoints return the thin ProviderResponse, so only merge
  // what they change rather than replacing the details.
  function mergeStatus(updated: ProviderResponse) {
    setProvider((prev) =>
      prev ? { ...prev, status: updated.status, updated_at: updated.updated_at } : prev
    )
  }

  async function handleResendInvite() {
    if (!slug || !provider) return
    setIsResending(true)
    try {
      await resendProviderInvite(slug, provider.id)
      showSuccess('Invitation resent successfully')
      await refreshProvider() // new invitation expiry
    } catch (err) {
      showError(errorMessage(err, 'Failed to resend invitation. Please try again.'))
    } finally {
      setIsResending(false)
    }
  }

  async function handleConfirmResetTwoFactor() {
    if (!slug || !provider) return
    setIsResettingTwoFactor(true)
    try {
      await resetProviderTwoFactor(slug, provider.id)
      setProvider((prev) => (prev ? { ...prev, two_factor_enabled: false } : prev))
      setResetTwoFactorOpen(false)
      showSuccess('Two-factor authentication reset')
    } catch (err) {
      showError(errorMessage(err, 'Failed to reset two-factor. Please try again.'))
    } finally {
      setIsResettingTwoFactor(false)
    }
  }

  async function handleEnable() {
    if (!slug || !provider) return
    setIsEnabling(true)
    try {
      mergeStatus(await updateProvider(slug, provider.id, { status: ProviderStatus.ACTIVE }))
      showSuccess('Provider enabled successfully')
    } catch (err) {
      showError(errorMessage(err, 'Failed to enable provider. Please try again.'))
    } finally {
      setIsEnabling(false)
    }
  }

  async function handleConfirmDisable() {
    if (!slug || !provider) return
    setIsDisabling(true)
    try {
      mergeStatus(await updateProvider(slug, provider.id, { status: ProviderStatus.DISABLED }))
      showSuccess('Provider disabled successfully')
      setDisableConfirmOpen(false)
    } catch (err) {
      showError(errorMessage(err, 'Failed to update provider status. Please try again.'))
    } finally {
      setIsDisabling(false)
    }
  }

  async function handleConfirmDelete() {
    if (!slug || !provider) return
    setIsDeleting(true)
    try {
      await deleteProvider(slug, provider.id)
      showSuccess('Provider deleted successfully')
      navigate(providersPath)
    } catch (err) {
      showError(errorMessage(err, 'Failed to delete provider. Please try again.'))
      setIsDeleting(false)
    }
  }

  async function handleManageCatalog() {
    if (!slug) return
    setCatalogModalOpen(true)
    try {
      const catalogResponse = await listCatalog(slug)
      setCatalogProducts(catalogResponse.products)
    } catch (err) {
      showError(errorMessage(err, 'Failed to load catalog data. Please try again.'))
      setCatalogModalOpen(false)
    }
  }

  async function handleSaveCatalogAssignments(productIds: string[]) {
    if (!slug || !provider) return
    setIsSavingCatalog(true)
    try {
      const updated = await updateProviderCatalogAssignments(slug, provider.id, {
        product_ids: productIds,
      })
      setAssignedProductIds(updated.product_ids)
      setProvider((prev) =>
        prev ? { ...prev, has_custom_catalog: updated.has_custom_catalog } : prev
      )
      showSuccess(
        productIds.length > 0
          ? `Custom catalog saved with ${productIds.length} product${productIds.length === 1 ? '' : 's'}`
          : 'Provider restored to full catalog'
      )
      setCatalogModalOpen(false)
    } catch (err) {
      showError(errorMessage(err, 'Failed to save catalog assignments. Please try again.'))
    } finally {
      setIsSavingCatalog(false)
    }
  }

  function handleCloseAddresses() {
    setAddressesModalOpen(false)
    setAddressesKey((k) => k + 1)
    void refreshProvider()
  }

  function goToOverrideSettings() {
    if (provider) navigate(`${providersPath}/${provider.id}/settings`)
  }

  /* ---- Derived display values ---- */

  const showSupervisor =
    !!provider &&
    (requiresSupervisingPhysician(provider.credential_type) ||
      !!provider.supervising_physician ||
      !!provider.supervising_physician_npi)

  const overrides = settings ? overridesFromResponse(settings) : null
  const overriddenSettings = overrides
    ? [
        ...generalSettingsFor(country),
        ...PAYMENT_DELIVERY_SETTINGS,
        ...PAYMENT_DELIVERY_REQUIRED_SETTINGS,
      ].filter((meta) => overrides[meta.key] !== null)
    : []

  // Same admin wiring as ProviderAddressesModal; read-only here, so only
  // `list` is ever called.
  const addressHandlers: PracticeAddressManagerHandlers | null =
    slug && provider
      ? {
          list: () => adminListProviderAddresses(slug, provider.id),
          create: (data) => adminCreateProviderAddress(slug, provider.id, data),
          update: (addressId, data) =>
            adminUpdateProviderAddress(slug, provider.id, addressId, data),
          remove: (addressId) => adminDeleteProviderAddress(slug, provider.id, addressId),
        }
      : null

  const invitationExpired =
    !!provider?.invitation_expires && new Date(provider.invitation_expires) < new Date()

  return (
    <div className="p-6 sm:p-8">
      <div className="max-w-4xl">
        {/* Back link */}
        <button
          type="button"
          onClick={() => navigate(providersPath)}
          className="mb-4 inline-flex items-center text-sm text-charcoal/70 hover:text-charcoal"
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to Providers
        </button>

        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="font-serif text-2xl text-charcoal sm:text-3xl break-words">
              {provider ? provider.name || provider.email : 'Provider Details'}
            </h1>
            {provider && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <ProviderStatusBadge status={provider.status} />
                {provider.credential_type && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white ring-1 ring-gray-200 text-charcoal">
                    {provider.credential_type.toUpperCase()}
                  </span>
                )}
                {provider.is_veterinarian && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white ring-1 ring-gray-200 text-charcoal">
                    Veterinarian
                  </span>
                )}
              </div>
            )}
          </div>

          {provider && (
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <Button variant="secondary" size="sm" onClick={goToOverrideSettings}>
                <Settings className="w-3.5 h-3.5 mr-1" />
                Override Settings
              </Button>
              {provider.status === ProviderStatus.INVITED && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleResendInvite}
                  loading={isResending}
                >
                  <Mail className="w-3.5 h-3.5 mr-1" />
                  Resend Invitation
                </Button>
              )}
              {provider.status === ProviderStatus.DISABLED && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleEnable}
                  loading={isEnabling}
                >
                  <UserCheck className="w-3.5 h-3.5 mr-1" />
                  Enable
                </Button>
              )}
              {canManage && provider.two_factor_enabled && (
                <Button variant="ghost" size="sm" onClick={() => setResetTwoFactorOpen(true)}>
                  <ShieldOff className="w-3.5 h-3.5 mr-1" />
                  Reset Two-Factor
                </Button>
              )}
              {provider.status === ProviderStatus.ACTIVE && (
                <Button variant="ghost" size="sm" onClick={() => setDisableConfirmOpen(true)}>
                  <UserX className="w-3.5 h-3.5 mr-1" />
                  Disable
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteConfirmOpen(true)}
                className="text-error hover:text-error"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <Card>
            <p className="py-8 text-center text-sm text-gray-500">Loading provider details…</p>
          </Card>
        ) : error || !provider ? (
          <Card>
            <div className="py-8 text-center">
              <p className="text-sm text-error">{error ?? 'Provider not found.'}</p>
              <Button variant="secondary" className="mt-4" onClick={load}>
                Retry
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card header="Contact">
                <dl className="space-y-3 text-sm">
                  <DetailRow
                    label="Email"
                    value={
                      <a
                        href={`mailto:${provider.email}`}
                        className="text-deep-purple hover:underline break-all"
                      >
                        {provider.email}
                      </a>
                    }
                  />
                  <DetailRow
                    label="Phone"
                    value={
                      provider.phone && (
                        <a href={`tel:${provider.phone}`} className="text-deep-purple hover:underline">
                          {formatPhoneForDisplay(provider.phone)}
                        </a>
                      )
                    }
                  />
                  <DetailRow label="Fax" value={formatPhoneForDisplay(provider.fax)} />
                </dl>
              </Card>

              <Card header="Credentials">
                <dl className="space-y-3 text-sm">
                  <DetailRow
                    label="Credential Type"
                    value={credentialTypeLabel(provider.credential_type)}
                  />
                  {usCredentials && (
                    <DetailRow
                      label="NPI"
                      value={
                        provider.npi && (
                          <code className="bg-gray-100 px-2 py-0.5 rounded">{provider.npi}</code>
                        )
                      }
                    />
                  )}
                  {usCredentials && (
                    <DetailRow
                      label="DEA Number"
                      value={
                        provider.dea_number && (
                          <code className="bg-gray-100 px-2 py-0.5 rounded">
                            {provider.dea_number}
                          </code>
                        )
                      }
                    />
                  )}
                  <DetailRow label="License Number" value={provider.license_number} />
                  <DetailRow
                    label={`License ${regionLabel(country ?? DEFAULT_COUNTRY)}`}
                    value={provider.license_state}
                  />
                  {showSupervisor && (
                    <DetailRow
                      label="Supervising Physician"
                      value={provider.supervising_physician}
                    />
                  )}
                  {showSupervisor && usCredentials && (
                    <DetailRow
                      label="Supervising Physician NPI"
                      value={provider.supervising_physician_npi}
                    />
                  )}
                  <DetailRow
                    label="Veterinarian"
                    value={provider.is_veterinarian ? 'Yes' : 'No'}
                  />
                </dl>
              </Card>
            </div>

            <Card
              headerContent={
                <SectionHeader
                  title="Practice Addresses"
                  action={
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setAddressesModalOpen(true)}
                    >
                      <MapPin className="w-3.5 h-3.5 mr-1" />
                      Manage
                    </Button>
                  }
                />
              }
            >
              {addressHandlers && (
                <PracticeAddressManager
                  key={addressesKey}
                  readOnly
                  showDefaults
                  handlers={addressHandlers}
                />
              )}
            </Card>

            <div className="grid gap-6 md:grid-cols-2">
              <Card header="Account">
                <dl className="space-y-3 text-sm">
                  <DetailRow
                    label="Profile"
                    value={
                      provider.profile_complete ? (
                        <span className="text-green-700">Complete</span>
                      ) : (
                        <span className="text-amber-700">Incomplete</span>
                      )
                    }
                  />
                  {provider.must_change_password && (
                    <DetailRow label="Password" value="Must change at next login" />
                  )}
                  <DetailRow
                    label="Two-Factor"
                    value={provider.two_factor_enabled ? 'On' : 'Off'}
                  />
                  {provider.status === ProviderStatus.INVITED && provider.invitation_expires && (
                    <DetailRow
                      label={invitationExpired ? 'Invitation Expired' : 'Invitation Expires'}
                      value={
                        <span className={invitationExpired ? 'text-error' : undefined}>
                          {formatDateTime(provider.invitation_expires)}
                        </span>
                      }
                    />
                  )}
                  <DetailRow label="Added" value={formatDate(provider.created_at)} />
                  <DetailRow label="Last Login" value={formatDateTime(provider.last_login_at)} />
                </dl>
              </Card>

              <Card
                headerContent={
                  <SectionHeader
                    title="Catalog Access"
                    action={
                      <Button variant="secondary" size="sm" onClick={handleManageCatalog}>
                        <Package className="w-3.5 h-3.5 mr-1" />
                        Manage
                      </Button>
                    }
                  />
                }
              >
                {provider.has_custom_catalog ? (
                  <>
                    <p className="text-sm font-medium text-charcoal">
                      Custom — {assignedProductIds.length} product
                      {assignedProductIds.length === 1 ? '' : 's'}
                    </p>
                    <p className="mt-1 text-sm text-charcoal/60">
                      This provider sees only the products assigned to them.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-charcoal">All products</p>
                    <p className="mt-1 text-sm text-charcoal/60">
                      This provider sees your full active catalog.
                    </p>
                  </>
                )}
              </Card>
            </div>

            <Card
              headerContent={
                <SectionHeader
                  title="Settings Overrides"
                  action={
                    <Button variant="secondary" size="sm" onClick={goToOverrideSettings}>
                      <Settings className="w-3.5 h-3.5 mr-1" />
                      Manage
                    </Button>
                  }
                />
              }
            >
              {overriddenSettings.length === 0 || !settings || !overrides ? (
                <p className="text-sm text-charcoal/60">
                  This provider follows your portal defaults for every setting.
                </p>
              ) : (
                <dl className="space-y-3 text-sm">
                  {overriddenSettings.map((meta) => (
                    <DetailRow
                      key={meta.key}
                      label={meta.label}
                      value={
                        <>
                          {overrides[meta.key] ? 'On' : 'Off'}
                          <span className="text-charcoal/50">
                            {' '}
                            (portal default: {settings.defaults[meta.key] ? 'On' : 'Off'})
                          </span>
                        </>
                      }
                    />
                  ))}
                </dl>
              )}
            </Card>
          </div>
        )}
      </div>

      <DisableProviderModal
        open={disableConfirmOpen}
        provider={provider}
        loading={isDisabling}
        onCancel={() => setDisableConfirmOpen(false)}
        onConfirm={handleConfirmDisable}
      />

      <ResetProviderTwoFactorModal
        open={resetTwoFactorOpen}
        provider={provider}
        loading={isResettingTwoFactor}
        onCancel={() => setResetTwoFactorOpen(false)}
        onConfirm={handleConfirmResetTwoFactor}
      />

      <DeleteProviderModal
        open={deleteConfirmOpen}
        provider={provider}
        loading={isDeleting}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      <CatalogAssignmentModal
        open={catalogModalOpen}
        onClose={() => setCatalogModalOpen(false)}
        onSave={handleSaveCatalogAssignments}
        provider={provider}
        products={catalogProducts}
        assignedProductIds={assignedProductIds}
        loading={isSavingCatalog}
      />

      {slug && (
        <ProviderAddressesModal
          open={addressesModalOpen}
          onClose={handleCloseAddresses}
          slug={slug}
          provider={provider}
          showCountryField={
            provider?.effective_allow_country_field ?? pharmacy?.allow_country_field ?? false
          }
          defaultCountry={country}
        />
      )}
    </div>
  )
}

export default ProviderDetail
